import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader } from 'ncoded-component-library';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import {
  DataRoomRFI,
  DataRoomRFICategory,
} from 'router/subrouters/DataRoom/models/DataRoomRfi';
import ArrowleftIcon from 'icons/Arrowleft.icon';
import {
  getHasRfiNewSubmission,
  getIsRfiCreationTypeChecklist,
  getIsRfiCreationTypeSpreadsheet,
  getIsRfiDisplayStatusClosed,
  getIsRfiDisplayStatusCompleted,
} from '../RfiList/components/RfiRequestsTable/utils';
import useRfiChangeStatus from 'router/subrouters/DataRoom/hooks/useRfiChangeStatus';
import RfiCategoriesTable from './components/RfiCategoriesTable';
import RfiCategoryDrawer from './components/RfiCategoryDrawer';
import RfiSettingsDrawer from './components/RfiSettingsDrawer';
import RfiActivityDrawer from './components/RfiActivityDrawer';
import RfiDetailsHeaderActions from './components/RfiDetailsHeaderActions';
import { getRfiActivityAccessDate } from './components/RfiActivityDrawer/utils';
import RfiInfo from './components/RfiInfo';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useTableFetchState from 'hooks/useTableFetchState';
import useQueryParams from 'hooks/useQueryParams';
import useDebounceSearch from 'hooks/useDebounceSearch';
import useCallbackRef from 'hooks/useCallbackRef';
import RfiContext, {
  RFI_LIST_STATUS_FILTER_ALL,
} from 'router/subrouters/DataRoom/providers/Rfi/Rfi.context';
import { SORTING_VALUES } from './components/RfiCategoriesTable/components/RfiSpreadsheetCategoriesTable/data';
import { DEFAULT_LIMIT, FIRST_PAGE, PAGE_PARAM_KEYWORD } from 'data/global';
import DownloadStatusContext from 'providers/DownloadStatus/DownloadStatus.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import downloadService from 'services/downloadService';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';
import useDrawerNavigation from 'hooks/useDrawerNavigation';

import './RfiDetails.styles.scss';

type RfiDetailsProps = {
  className?: string;
};

const RfiDetails: React.FC<RfiDetailsProps> = (props) => {
  const { className } = props;

  const { id } = useParams();

  const [isRfiLoading, setIsRfiLoading] = useState(true);
  const [rfi, setRfi] = useState<DataRoomRFI>();
  const [assignedEmails, setAssignedEmails] = useState<string[]>([]);

  const [selectedCategory, setSelectedCategory] =
    useState<DataRoomRFICategory>();

  const [activityDrawer, activityDrawerRef] = useCallbackRef<SideDrawerRef>();
  const [categoryDrawer, categoryDrawerRef] = useCallbackRef<SideDrawerRef>();
  const [settingsDrawer, settingsDrawerRef] = useCallbackRef<SideDrawerRef>();

  const { creationType } = rfi || {};

  const {
    isLoading,
    totalPages,
    totalItems,
    data,
    setData,
    setIsLoading,
    setPaginatedData,
  } = useTableFetchState<DataRoomRFICategory>();

  const {
    params: { sort = SORTING_VALUES.name, page },
    setQueryParam,
  } = useQueryParams();

  const clearPage = useCallback(
    () => setQueryParam(PAGE_PARAM_KEYWORD, FIRST_PAGE),
    [setQueryParam],
  );

  const { searchInputValue, searchString, onSearchInputChange } =
    useDebounceSearch({ onSearch: clearPage });

  const [categoriesStatusFilter, setCategoriesStatusFilter] = useState<string>(
    RFI_LIST_STATUS_FILTER_ALL,
  );

  const onCategoriesStatusFilterChange = useCallback(
    (value: string) => {
      setCategoriesStatusFilter(value);
      clearPage();
    },
    [clearPage],
  );

  const isReassignEnabled =
    !getIsRfiDisplayStatusClosed(rfi?.displayStatus) &&
    !getIsRfiDisplayStatusCompleted(rfi?.displayStatus);

  const canReassign =
    !!rfi && getIsRfiCreationTypeSpreadsheet(creationType) && isReassignEnabled;

  const onCategoryRowClick = useCallback(
    (category: DataRoomRFICategory) => {
      setSelectedCategory(category);
      categoryDrawer?.setOpen(true);
    },
    [categoryDrawer],
  );

  const { onPrev, onNext } = useDrawerNavigation({
    rows: data,
    selectedId: selectedCategory?._id,
    totalPages,
    isLoading,
    onNavigate: onCategoryRowClick,
  });

  const onActivityClick = useCallback(
    () => activityDrawer?.setOpen(true),
    [activityDrawer],
  );

  const onSettingsClick = useCallback(
    () => settingsDrawer?.setOpen(true),
    [settingsDrawer],
  );

  const { startRfiCategoriesExportDownload, failRfiCategoriesExportDownload } =
    useContext(DownloadStatusContext);
  const { popServerError } = useContext(PopNotificationsContext);
  const { updateRfiListItem } = useContext(RfiContext);
  const { dataRoom, fetchRfiUnseenCount } = useContext(DataRoomContext);

  const getMixpanelProps = useGetMixpanelProps();

  const accessDate = useMemo(
    () =>
      getRfiActivityAccessDate(
        dataRoom?.currentMember?.rfiActivityAccessDates,
        rfi?._id,
      ),
    [dataRoom?.currentMember?.rfiActivityAccessDates, rfi?._id],
  );

  const onRfiUpdate = useCallback(
    (updatedRfi: DataRoomRFI) => {
      setRfi((prev) => ({ ...prev, ...updatedRfi }));
      updateRfiListItem(updatedRfi);
    },
    [updateRfiListItem],
  );

  const { changeStatus, loading: isChangeStatusLoading } = useRfiChangeStatus(
    rfi,
    onRfiUpdate,
  );

  const { t } = useTranslation();

  const classes = classNames('rfi-details', 'white-panel', className);

  useEffect(() => {
    (async () => {
      setIsRfiLoading(true);

      try {
        const { data } = await dataRoomApi.rfi.getRfiById(id);
        setRfi(data);

        if (getHasRfiNewSubmission(data)) {
          await dataRoomApi.rfi.viewRfi(data._id);
          updateRfiListItem({ ...data, lastOpenedAt: new Date() });
          fetchRfiUnseenCount();
        }
      } catch (e) {
        mixpanelService.errorViewed(
          getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
        );
      } finally {
        setIsRfiLoading(false);
      }
    })();
  }, [id, updateRfiListItem, fetchRfiUnseenCount, getMixpanelProps]);

  useEffect(() => {
    if (!canReassign) return;

    (async () => {
      try {
        const { data } = await dataRoomApi.rfi.getAssignedEmails(id);

        setAssignedEmails(data.emails);
      } catch (e) {
        mixpanelService.errorViewed(
          getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
        );
      }
    })();
  }, [id, canReassign, getMixpanelProps]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      try {
        const { data } = await dataRoomApi.rfi.getRfiCategories(id, {
          $page: page,
          $limit: DEFAULT_LIMIT,
          $sort: sort,
          $searchString: searchString,
          ...(categoriesStatusFilter !== RFI_LIST_STATUS_FILTER_ALL && {
            uploadStatus: categoriesStatusFilter,
          }),
        });

        setPaginatedData(data);
      } catch (e) {
        return;
      } finally {
        setIsLoading(false);
      }
    })();
  }, [
    id,
    page,
    sort,
    searchString,
    categoriesStatusFilter,
    setIsLoading,
    setPaginatedData,
  ]);

  const onCategoryUpdate = useCallback(
    (updatedCategory: DataRoomRFICategory) => {
      setData((prev) =>
        prev.map((category) =>
          category._id === updatedCategory._id
            ? { ...category, ...updatedCategory }
            : category,
        ),
      );

      setSelectedCategory((prev) =>
        prev?._id === updatedCategory._id
          ? { ...prev, ...updatedCategory }
          : prev,
      );
    },
    [setData],
  );

  const onExport = useCallback(async () => {
    try {
      startRfiCategoriesExportDownload();

      await dataRoomApi.rfi.exportRfiCategories(
        id,
        downloadService.downloadUniqueId,
        {
          ...(searchString && { $searchString: searchString }),
          ...(categoriesStatusFilter !== RFI_LIST_STATUS_FILTER_ALL && {
            uploadStatus: categoriesStatusFilter,
          }),
        },
      );
    } catch (e) {
      failRfiCategoriesExportDownload();
      popServerError(e);
    }
  }, [
    id,
    searchString,
    categoriesStatusFilter,
    startRfiCategoriesExportDownload,
    failRfiCategoriesExportDownload,
    popServerError,
  ]);

  if (isRfiLoading)
    return (
      <div className={classes}>
        <Loader overlay={false} />
      </div>
    );

  return (
    <div className={classes}>
      <Link to="../list" className="rfi-details__back">
        <ArrowleftIcon />
        {t('backToFileRequests')}
      </Link>

      <div className="rfi-details__header">
        <RfiInfo rfi={rfi} onRfiUpdate={onRfiUpdate} />

        <RfiDetailsHeaderActions
          rfi={rfi}
          accessDate={accessDate}
          isChangeStatusLoading={isChangeStatusLoading}
          onExport={onExport}
          onChangeStatus={changeStatus}
          onActivityClick={onActivityClick}
          onSettingsClick={onSettingsClick}
        />
      </div>

      <RfiCategoriesTable
        isLoading={isLoading}
        data={data}
        totalPages={totalPages}
        totalItems={totalItems}
        creationType={creationType}
        searchInputValue={searchInputValue}
        statusFilter={categoriesStatusFilter}
        assignedEmails={assignedEmails}
        isReassignEnabled={isReassignEnabled}
        onCategoryUpdate={onCategoryUpdate}
        onSearchInputChange={onSearchInputChange}
        onStatusFilterChange={onCategoriesStatusFilterChange}
        onRowClick={onCategoryRowClick}
      />

      <RfiCategoryDrawer
        ref={categoryDrawerRef}
        category={selectedCategory}
        isChecklist={getIsRfiCreationTypeChecklist(creationType)}
        onCategoryUpdate={onCategoryUpdate}
        onPrev={onPrev}
        onNext={onNext}
      />

      <RfiSettingsDrawer
        ref={settingsDrawerRef}
        rfi={rfi}
        onRfiUpdate={onRfiUpdate}
      />

      <RfiActivityDrawer
        ref={activityDrawerRef}
        rfi={rfi}
        accessDate={accessDate}
        onRfiUpdate={onRfiUpdate}
      />
    </div>
  );
};

export default RfiDetails;
